import { useState, useEffect } from 'react';

import { useGetExternalLoyaltyManualOfferQuery } from '@occc/graphql';

import {
  TOBACCO_OFFERS_LIST_POLL_INTERVAL_MS,
  TOBACCO_OFFERS_LIST_POLL_TIMEOUT_MS,
} from '../tobacco-api-offers.constants';

type PollingMode = 'create' | 'delete';

type UsePendingOfferPollingResult = {
  isListPollingEnabled: boolean;
  pendingOfferUuid: string | null;
  startPolling: (uuid: string, mode: PollingMode) => void;
};

export const usePendingOfferPolling = (): UsePendingOfferPollingResult => {
  const [isListPollingEnabled, setIsListPollingEnabled] = useState(false);
  const [pendingOfferUuid, setPendingOfferUuid] = useState<string | null>(null);
  const [pollingMode, setPollingMode] = useState<PollingMode>('create');

  const pendingOfferQuery = useGetExternalLoyaltyManualOfferQuery({
    variables: { uuid: pendingOfferUuid ?? '' },
    skip: !isListPollingEnabled || !pendingOfferUuid,
    pollInterval:
      isListPollingEnabled && pendingOfferUuid ? TOBACCO_OFFERS_LIST_POLL_INTERVAL_MS : undefined,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!isListPollingEnabled || !pendingOfferUuid) return;
    const offer = pendingOfferQuery.data?.externalLoyaltyManualOffer;
    if (!offer) return;

    const isDone =
      pollingMode === 'delete'
        ? offer.isArchived || offer.hasError
        : offer.inActivate || offer.hasError;

    if (isDone) {
      setIsListPollingEnabled(false);
      setPendingOfferUuid(null);
    }
  }, [isListPollingEnabled, pendingOfferUuid, pollingMode, pendingOfferQuery.data]);

  useEffect(() => {
    if (!isListPollingEnabled) return;
    const timeoutId = window.setTimeout(() => {
      setIsListPollingEnabled(false);
      setPendingOfferUuid(null);
    }, TOBACCO_OFFERS_LIST_POLL_TIMEOUT_MS);
    return () => window.clearTimeout(timeoutId);
  }, [isListPollingEnabled]);

  const startPolling = (uuid: string, mode: PollingMode) => {
    setPollingMode(mode);
    setPendingOfferUuid(uuid);
    setIsListPollingEnabled(true);
  };

  return { isListPollingEnabled, pendingOfferUuid, startPolling };
};
